import {ServiceBase} from "../ServiceBase";
import {IStateOfResidenceIdentifier} from "./state-of-residence-identifier.interface";
import React from "react";
import IStateOfResidence from "../../../common/interfaces/state-of-residence.interface";
import {
    IStatePopupDisplayParams
} from "../../../common/interfaces/orbit-component-to-sdk-adapter/components-props/state-popup-display-params";
import {OneSiteStatePopupStrategy, RegularStatePopupStrategy, StatePopupStrategy} from "./state-popup-strategies";
import {Check} from "@sparkware/app-utils";
import {STATE_OF_RESIDENCE_COOKIE_KEY} from "../../../common/const/state-of-residence-cookie_const";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";

export class StateOfResidenceIdentifierService extends ServiceBase implements IStateOfResidenceIdentifier {

    private _statePopupStrategy: StatePopupStrategy = null;
    private _shouldShowLoginFormSessionStorageKey: string = "shouldShowLoginFormOnInit";

    constructor(services: IServiceFactoryExtended) {
        super(services);

        if (this.services.location.currentUri.queryParams.hasParam(this._cookieKey)){
            this._onReceivedUserStateQueryParam(this.services.location.currentUri.queryParams.getParamValue(this._cookieKey));
        }

        /* We need this because, otherwise, we have a circular dependency between services:
        This service wants to open the login form, but then the login form checks whether it should
        display the state popup beforehand, that check is also done here in this service.
        */
        setTimeout(this._displayLoginForm, 0);
    }

    private _onReceivedUserStateQueryParam = (userSelectedState: string) => {
        this.saveStateInCookie(userSelectedState);
        const urlWithoutUserState = this.services.location.currentUri.removeSearchParam(this._cookieKey).toString();
        this.services.location.replace(urlWithoutUserState).catch((err) => {
            this.services.logger.error('Remove userSelectedState query param from URL failed!', err);
        });
    }

    private _displayLoginForm = async () => {
        if (this.services.sessionStorage.getItem(this._shouldShowLoginFormSessionStorageKey)) {
            this.services.sessionStorage.removeItem(this._shouldShowLoginFormSessionStorageKey);
            await this.services.user.waitForAutoLogin();
            if (!this.services.user.isAuthorized) {
               await this.services.user.openLogin();
            }
        }
    }

    get statePopupStrategy(): StatePopupStrategy {
        if (!Check.isNullOrUndefined(this._statePopupStrategy)) {
            return this._statePopupStrategy;
        }

        if (this.isOneSiteEnabled()){
            this._statePopupStrategy = new OneSiteStatePopupStrategy(this.services, this._shouldShowLoginFormSessionStorageKey);
        } else {
            this._statePopupStrategy = new RegularStatePopupStrategy(this.services);
        }

        return this._statePopupStrategy;
    }

    displayStateSelectorPopup(props: IStatePopupDisplayParams = {}): Promise<void> {
        this.services.sideMenu.closeMobileMenu();

        return new Promise((resolve, reject) => {
            if (!this.statePopupStrategy.shouldDisplayStatePopup(props.forceDisplay)) {
                return resolve();
            }

            const StatePopup = this.statePopupStrategy.getComponent();

            this.services.dialog.showModal(dialogHandler =>
                <StatePopup
                    dialogHandler={dialogHandler}
                    onStateSelect={(stateID)=> {
                        this.statePopupStrategy.onStateConfirmation(stateID);
                        return resolve();
                    }}
                    onStateNotInListClick={()=> {
                        this.statePopupStrategy.onOtherStateClick();
                        reject();
                    }}
                />, {
                title: this.statePopupStrategy.getPopupTitle(),
                icon: this.services.theme.currentTheme.icons.locationIcon,
                forceTransparentBackground: true,
                disableFullscreenMobile: true,
                forceCentralization: true,
                mobileCenterHeader: true,
                isHighPriority:  true,
                onClosed: () => {
                    this.services.sdk.eventsTrigger.locationPopupClosed();
                    return reject();
                },
                shouldBeClosedOnlyByUser: true
            });
        });
    }

    private _cookieKey = STATE_OF_RESIDENCE_COOKIE_KEY;

    saveStateInCookie(stateID: string): void {
        this.removeStateCookie();
        this.services.cookies.createCookie(this._cookieKey, stateID);
    }

    removeStateCookie(): void {
        if (this.services.cookies.getCookie(this._cookieKey)) {
            this.services.cookies.createCookie(this._cookieKey, '');
        }
    }

    //The active state can come either from the cookie(chosen by user in the dropdown)
    //Or from the server, in case site infra detects it by IP.
    getUserSelectedState(): string {
        return this.getStateFromCookie() || this.services.configuration.userSelectedState || "";
    }

    getStateFromCookie(): string {
        return this.services.cookies.getCookie(this._cookieKey) || "";
    }

    getSupportedStates(): IStateOfResidence[] {
        return this.services.sdk.getSupportedStates();
    }

    isOneSiteEnabled(): boolean {
        return this.services.configuration.data?.oneSite?.isEnabled && this.getSupportedStates().length > 0;
    }

    shouldDisplayStateSelectorButton(): boolean {
        return this.isOneSiteEnabled() && !this.services.user.isAuthorized;
    }

    prepareUrlForExternalBrand(sourceLink: string): string {
        const userSelectedState = this.getStateFromCookie();
        if (this.isOneSiteEnabled() && !Check.isEmpty(userSelectedState)){
            const url = new URL(sourceLink);
            url.searchParams.append(STATE_OF_RESIDENCE_COOKIE_KEY, userSelectedState);
            return url.href;
        }
        return sourceLink;
    }
}

